import type {
  Account,
  Goal,
  PlanAssumptions,
  PlanEvent,
  PlanHorizon,
  RetirementProfile,
} from '@/api/generated/model';
import { goalCashImpactForYear } from './goalProjection';
import {
  resolveIncomeSources,
  sumIncomeSourcesForYear,
  type IncomeSourceYearAmount,
} from './incomeSources';
import { resolveRecurrentPayments, sumRecurrentForYear } from './recurrentPayments';
import {
  filterYearsByIncrement,
  getProjectionYearsForRange,
  type PlanTimeIncrement,
} from './timePeriod';

export type AssetClass = 'cash' | 'crypto' | 'equity' | 'realEstate' | 'other';

export interface YearlyNetWorthPoint {
  year: number;
  label: string;
  total: number;
  byClass: Record<AssetClass, number>;
  byAccount: Record<string, number>;
}

export interface CashFlowYear {
  year: number;
  income: number;
  taxes: number;
  expenses: number;
  net: number;
  incomeBySource?: IncomeSourceYearAmount[];
}

export interface DrawdownYear {
  year: number;
  age: number;
  spending: number;
  statePension: number;
  drawdownByWrapper: Record<string, number>;
  drawdownByAccount: Record<string, number>;
  remainingByAccount: Record<string, number>;
}

export interface RetirementYearPoint {
  year: number;
  age: number;
  phase: 'accumulation' | 'drawdown';
  totalContributions: number;
  totalDrawdown: number;
  contributionsByAccount: Record<string, number>;
  drawdownByAccount: Record<string, number>;
  spending: number;
  statePension: number;
}

export interface ProjectionInput {
  accounts: Account[];
  assumptions: PlanAssumptions;
  /** @deprecated use goals */
  events?: PlanEvent[];
  goals?: Goal[];
  retirement: RetirementProfile;
  horizon?: PlanHorizon;
  rangeYears?: number;
  increment?: PlanTimeIncrement;
  birthYear?: number;
}

export interface ProjectionResult {
  netWorth: YearlyNetWorthPoint[];
  cashFlow: CashFlowYear[];
  drawdown: DrawdownYear[];
  retirementTimeline: RetirementYearPoint[];
  currentNetWorth: number;
  projectedNetWorthEnd: number;
  projectedNetWorthAtLifeExpectancy: number;
}

/** Shortest retirement span used when dividing the pot into annual spend. */
const MIN_COVERAGE_YEARS = 10;
/** Longer rule-of-thumb span — used as the slider floor band with short coverage. */
const TYPICAL_COVERAGE_YEARS = 15;
/** Absolute UI ceiling so extreme pots cannot produce multi‑million sliders. */
const ANNUAL_SPENDING_CAP_CEILING = 500_000;
const ANNUAL_SPENDING_CAP_FLOOR = 12_000;

export interface AnnualSpendingMaxInput {
  projection: Pick<
    ProjectionResult,
    'currentNetWorth' | 'projectedNetWorthAtLifeExpectancy' | 'netWorth'
  >;
  retirementAge: number;
  lifeExpectancyAge: number;
  /** Preferred pot when Monte Carlo median at retirement is available. */
  medianPotAtRetirement?: number;
  birthYear?: number;
  currentAge?: number;
  statePensionAnnualAmount?: number;
}

function ageForNetWorthPoint(
  point: YearlyNetWorthPoint,
  birthYear: number | undefined,
  currentAge: number | undefined
): number {
  if (birthYear != null) return point.year - birthYear;
  if (currentAge != null) {
    return currentAge + (point.year - new Date().getFullYear());
  }
  return point.year;
}

/** Projected pot at retirement age, else peak / current balances. */
export function potAtRetirement(
  projection: Pick<ProjectionResult, 'currentNetWorth' | 'netWorth'>,
  retirementAge: number,
  birthYear?: number,
  currentAge?: number
): number {
  const atRetirement = projection.netWorth.find(
    (point) => ageForNetWorthPoint(point, birthYear, currentAge) >= retirementAge
  );
  if (atRetirement) return Math.max(0, atRetirement.total);

  const peak = projection.netWorth.reduce(
    (max, point) => Math.max(max, point.total),
    projection.currentNetWorth
  );
  return Math.max(0, peak, projection.currentNetWorth);
}

/**
 * Upper bound for the annual retirement spending control.
 *
 * Treats the retirement pot as covering the rest of life (typically ~10–15+
 * years): slider max ≈ pot ÷ 10 years (+ state pension), so the control tracks
 * starting / median pot size instead of raw peak net worth.
 */
export function computeAnnualSpendingMax(input: AnnualSpendingMaxInput): number {
  const {
    projection,
    retirementAge,
    lifeExpectancyAge,
    medianPotAtRetirement,
    birthYear,
    currentAge,
    statePensionAnnualAmount = 0,
  } = input;

  const pot = Math.max(
    0,
    medianPotAtRetirement ?? potAtRetirement(projection, retirementAge, birthYear, currentAge)
  );

  // Rest-of-life years (floored at 10). Clamp into the 10–15y planning band so
  // annual spend ≈ pot ÷ years of retirement expenses, not the whole pot.
  const remainingYears = Math.max(lifeExpectancyAge - retirementAge, MIN_COVERAGE_YEARS);
  const coverageYears = Math.min(
    Math.max(remainingYears, MIN_COVERAGE_YEARS),
    TYPICAL_COVERAGE_YEARS
  );
  const annualFromPot = pot / coverageYears;

  const pension = Math.max(0, statePensionAnnualAmount);
  const capped = Math.min(ANNUAL_SPENDING_CAP_CEILING, annualFromPot + pension);
  const rounded = Math.ceil(capped / 1000) * 1000;
  return Math.max(ANNUAL_SPENDING_CAP_FLOOR, rounded);
}

export function clampAnnualSpending(value: number, max: number): number {
  return Math.min(Math.max(0, value), max);
}

export function getHorizonYears(horizon: PlanHorizon): number {
  return horizon === 'oneYear' ? 1 : 10;
}

export function getProjectionYears(horizon: PlanHorizon): number[] {
  return getProjectionYearsForRange(getHorizonYears(horizon));
}

function resolveRangeYears(input: ProjectionInput): number {
  if (input.rangeYears != null) {
    return input.rangeYears;
  }
  if (input.horizon) {
    return getHorizonYears(input.horizon);
  }
  return 10;
}

function growthRateForAccount(
  accountId: string,
  assumptions: PlanAssumptions,
  accountType: string
): number {
  const custom = assumptions.accountGrowthRates.find((r) => r.accountId === accountId);
  if (custom) {
    return custom.annualGrowthRate;
  }
  switch (accountType) {
    case 'real_estate':
    case 'property':
      return 0.03;
    case 'savings':
    case 'checking':
    case 'bank':
      return 0.01;
    default:
      return 0;
  }
}

/** Ordered account IDs used for retirement drawdown (explicit list, wrapper order, or all assets). */
export function resolveDrawdownAccountIds(
  retirement: RetirementProfile,
  accounts: Account[]
): string[] {
  if (retirement.drawdownAccountIds?.length) {
    return retirement.drawdownAccountIds;
  }
  if (retirement.drawdownOrder?.length) {
    const ids: string[] = [];
    for (const wrapper of retirement.drawdownOrder) {
      for (const account of accounts) {
        if (account.type === 'liability') continue;
        if (wrapperMatchesAccount(wrapper, account.type) && !ids.includes(account.id)) {
          ids.push(account.id);
        }
      }
    }
    return ids;
  }
  return accounts.filter((a) => a.type !== 'liability').map((a) => a.id);
}

/** Annual contribution for an account from plan assumptions (0 if unset). */
export function contributionForAccount(accountId: string, assumptions: PlanAssumptions): number {
  const custom = assumptions.accountGrowthRates.find((r) => r.accountId === accountId);
  return custom?.annualContribution ?? 0;
}

export function computeProjections(input: ProjectionInput): ProjectionResult {
  const rangeYears = resolveRangeYears(input);
  const allYears = getProjectionYearsForRange(rangeYears);
  const years = filterYearsByIncrement(allYears, input.increment ?? '1Y');
  const birthYear = input.birthYear ?? new Date().getFullYear() - 40;
  const goals = input.goals ?? [];
  const recurrentPayments = resolveRecurrentPayments(input.assumptions);
  const hasExplicitRecurrent = (input.assumptions.recurrentPayments?.length ?? 0) > 0;
  const incomeSources = resolveIncomeSources(input.assumptions);
  const drawdownAccountIds = resolveDrawdownAccountIds(input.retirement, input.accounts);
  const lifeExpectancyAge = input.assumptions.lifeExpectancyAge ?? 90;

  const assetAccounts = input.accounts.filter((a) => a.type !== 'liability');
  const currentNetWorth =
    assetAccounts.reduce((s, a) => s + a.balance, 0) -
    input.accounts.filter((a) => a.type === 'liability').reduce((s, a) => s + a.balance, 0);

  const accountBalances: Record<string, number> = {};
  for (const account of input.accounts) {
    accountBalances[account.id] = account.type === 'liability' ? -account.balance : account.balance;
  }

  const netWorth: YearlyNetWorthPoint[] = [];
  const cashFlow: CashFlowYear[] = [];
  const drawdown: DrawdownYear[] = [];
  const retirementTimeline: RetirementYearPoint[] = [];
  let netWorthAtLifeExpectancy = currentNetWorth;

  for (let yearIndex = 0; yearIndex < years.length; yearIndex++) {
    const year = years[yearIndex];
    const age = year - birthYear;
    const isRetired = age >= input.retirement.retirementAge;

    const contributionsByAccount: Record<string, number> = {};
    const drawdownByAccount: Record<string, number> = {};

    if (!isRetired) {
      for (const account of input.accounts) {
        if (account.type === 'liability') continue;
        const contribution = contributionForAccount(account.id, input.assumptions);
        if (contribution > 0) {
          accountBalances[account.id] = (accountBalances[account.id] ?? 0) + contribution;
          contributionsByAccount[account.id] = contribution;
        }
      }
    }

    for (const account of input.accounts) {
      const rate = growthRateForAccount(account.id, input.assumptions, account.type);
      accountBalances[account.id] = (accountBalances[account.id] ?? 0) * (1 + rate);
    }

    const goalImpact = goalCashImpactForYear(goals, year);
    if (goalImpact !== 0) {
      const primary = assetAccounts[0];
      if (primary) {
        accountBalances[primary.id] = (accountBalances[primary.id] ?? 0) + goalImpact;
      }
    }

    const { income: recurrentIncome, expenses: recurrentExpenses } = sumRecurrentForYear(
      recurrentPayments,
      yearIndex
    );

    let income: number;
    let expenses: number;
    let incomeBySource: IncomeSourceYearAmount[] | undefined;

    if (!isRetired) {
      if (hasExplicitRecurrent) {
        income = recurrentIncome;
      } else if (incomeSources.length > 0) {
        const sourceTotals = sumIncomeSourcesForYear(incomeSources, yearIndex);
        income = sourceTotals.total;
        incomeBySource = sourceTotals.bySource;
      } else {
        income = recurrentIncome;
      }
      expenses = recurrentExpenses;
    } else {
      income =
        age >= input.retirement.statePensionAge ? input.retirement.statePensionAnnualAmount : 0;
      expenses = input.retirement.annualSpendingTarget;
    }

    const taxes = income * input.assumptions.effectiveTaxRate;
    const net = income - taxes - expenses;

    if (!isRetired && net !== 0) {
      const cashAccount =
        input.accounts.find((a) => a.type === 'checking' || a.type === 'savings') ??
        assetAccounts[0];
      if (cashAccount) {
        accountBalances[cashAccount.id] = (accountBalances[cashAccount.id] ?? 0) + net;
      }
    }

    cashFlow.push({ year, income, taxes, expenses, net, incomeBySource });

    // Apply this year's retirement drawdown BEFORE snapshotting net worth, so a
    // drawdown year's net worth reflects the spending (mirrors how accumulation
    // contributions are already applied before the snapshot above).
    let spending = 0;
    let statePension = 0;

    if (isRetired) {
      statePension =
        age >= input.retirement.statePensionAge ? input.retirement.statePensionAnnualAmount : 0;
      spending = input.retirement.annualSpendingTarget;
      let remaining = spending - statePension;

      const drawdownByWrapper: Record<string, number> = {};
      for (const accountId of drawdownAccountIds) {
        if (remaining <= 0) break;
        const available = Math.max(0, accountBalances[accountId] ?? 0);
        const take = Math.min(available, remaining);
        accountBalances[accountId] = available - take;
        drawdownByAccount[accountId] = take;
        drawdownByWrapper[accountId] = take;
        remaining -= take;
      }

      drawdown.push({
        year,
        age,
        spending,
        statePension,
        drawdownByWrapper,
        drawdownByAccount,
        remainingByAccount: { ...accountBalances },
      });
    }

    const byAccount = { ...accountBalances };
    const byClass: Record<AssetClass, number> = {
      cash: 0,
      crypto: 0,
      equity: 0,
      realEstate: 0,
      other: 0,
    };

    for (const account of input.accounts) {
      const bal = byAccount[account.id] ?? 0;
      byClass[mapAccountTypeToClass(account.type)] += bal;
    }

    const total = Object.values(byAccount).reduce((s, v) => s + v, 0);
    netWorth.push({
      year,
      label: String(year),
      total,
      byClass,
      byAccount,
    });

    if (age === lifeExpectancyAge) {
      netWorthAtLifeExpectancy = total;
    }

    const totalContributions = Object.values(contributionsByAccount).reduce((s, v) => s + v, 0);
    const totalDrawdown = Object.values(drawdownByAccount).reduce((s, v) => s + v, 0);

    retirementTimeline.push({
      year,
      age,
      phase: isRetired ? 'drawdown' : 'accumulation',
      totalContributions,
      totalDrawdown,
      contributionsByAccount,
      drawdownByAccount,
      spending,
      statePension,
    });
  }

  const projectedNetWorthEnd = netWorth[netWorth.length - 1]?.total ?? currentNetWorth;

  return {
    netWorth,
    cashFlow,
    drawdown,
    retirementTimeline,
    currentNetWorth,
    projectedNetWorthEnd,
    projectedNetWorthAtLifeExpectancy: netWorthAtLifeExpectancy,
  };
}

function wrapperMatchesAccount(wrapper: string, accountType: string): boolean {
  switch (wrapper) {
    case 'isa':
      return accountType === 'isa' || accountType === 'investment';
    case 'sipp':
    case 'pension':
      return accountType === 'retirement' || accountType === 'pension';
    case 'cash':
      return accountType === 'bank' || accountType === 'checking' || accountType === 'savings';
    case 'gia':
      return accountType === 'investment' || accountType === 'brokerage';
    default:
      return false;
  }
}

export function mapAccountTypeToClass(type: string): AssetClass {
  switch (type) {
    case 'crypto':
      return 'crypto';
    case 'investment':
    case 'retirement':
    case 'brokerage':
    case 'pension':
    case 'isa':
      return 'equity';
    case 'real_estate':
    case 'property':
    case 'real-estate':
      return 'realEstate';
    case 'bank':
    case 'checking':
    case 'savings':
      return 'cash';
    default:
      return 'other';
  }
}

export const ASSET_CLASS_ORDER: AssetClass[] = ['cash', 'crypto', 'equity', 'realEstate', 'other'];

export const PLAN_EVENT_CATEGORIES = [
  'career',
  'realEstate',
  'lifestyle',
  'borrowing',
  'equity',
  'estate',
  'philanthropy',
  'retirement',
] as const;

export type PlanningTab =
  | 'overview'
  | 'cash-flow'
  | 'net-worth'
  | 'goals'
  | 'events'
  | 'retirement'
  | 'succession';

export const PLANNING_TABS: PlanningTab[] = [
  'overview',
  'cash-flow',
  'net-worth',
  'goals',
  'events',
  'retirement',
  'succession',
];
